// World renderer: draws room, beds, NPCs, bubbles, floaters on canvas.
// Pure-ish draw pass (mutates only the canvas + the rect/floater arrays it
// is handed). Extracted from GameScreen to respect the line limit.
import { TUNING } from '../constants.js';
import { clampBubbleX, clampTailX, layoutBubbles, speakerAnchor, speakerBorderColor, waitingPairs } from '../sim/bubbles.js';
import type { ActiveConvo } from './useConversations.js';
import type { WorldState } from './useSim.js';
import type { Npc } from '../types.js';
import {
  canvasDimsFor,
  convoAnchor,
  drawBeds,
  drawBubble,
  drawFloaters,
  drawNpc,
  drawRoom,
  drawWaiting,
  measureBubble,
  ROOM_PAD_TOP,
  ROOM_PAD_X,
  wrapBubbleText,
} from './draw.js';

// Re-exported so GameScreen + world share one canvas geometry.
export { canvasDimsFor, ROOM_PAD_TOP, ROOM_PAD_X };

export interface FloaterState {
  id: string;
  npcId: string;
  items: Array<{ label: string; up: boolean }>;
  born: number; // sim ms
}

export interface BubbleRect {
  id: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function placeSpeakerBubble(
  g: CanvasRenderingContext2D,
  speaker: string,
  text: string,
  convo: { aId: string; bId: string },
  runtime: WorldState['runtime'],
  byId: Map<string, Npc>,
  padX: number,
  padTop: number,
  canvasW: number,
  lane: number,
): { cx: number; cy: number; bw: number; bh: number; tailX: number } {
  const ra = runtime.find((r) => r.npcId === convo.aId);
  const rb = runtime.find((r) => r.npcId === convo.bId);
  const nameOf = (id: string): string => {
    const n = byId.get(id);
    return n ? `${n.firstName} ${n.lastName}` : '';
  };
  const head = speakerAnchor({
    speaker,
    aName: nameOf(convo.aId),
    bName: nameOf(convo.bId),
    ax: ra?.x ?? 0,
    ay: ra?.y ?? 0,
    bx: rb?.x ?? 0,
    by: rb?.y ?? 0,
  });
  const measured = measureBubble(g, wrapBubbleText(g, text || ' '));
  const cx = clampBubbleX(head.x + padX, measured.w, canvasW) - padX;
  const cy = Math.max(head.y - 52 - lane * 64, measured.h - padTop + 6);
  return { cx, cy, bw: measured.w, bh: measured.h, tailX: clampTailX(head.x, cx, measured.w) };
}

export function renderWorld(args: {
  canvas: HTMLCanvasElement;
  w: WorldState;
  npcs: Npc[];
  active: ActiveConvo[];
  walk: Map<string, number>;
  floaters: FloaterState[];
  hoverConvoId: string | null;
  borderColors?: Record<string, string>;
}): { floaters: FloaterState[]; rects: BubbleRect[] } {
  const { canvas, w, npcs, active, walk } = args;
  const isRect = w.width != null && w.height != null;
  const padX = isRect ? 0 : ROOM_PAD_X;
  const padTop = isRect ? 0 : ROOM_PAD_TOP;
  const dims = isRect ? { w: w.width!, h: w.height! } : canvasDimsFor(w.size);
  if (canvas.width !== dims.w || canvas.height !== dims.h) {
    canvas.width = dims.w;
    canvas.height = dims.h;
  }
  const g = canvas.getContext('2d')!;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, canvas.width, canvas.height);
  if (!isRect) g.translate(ROOM_PAD_X, ROOM_PAD_TOP);
  drawRoom(g, isRect ? w.width! : w.size, isRect ? w.height! : w.size, TUNING.wallThicknessPx);
  const byId = new Map(npcs.map((n) => [n.id, n]));
  drawBeds(g, w.beds, new Set(w.runtime.filter((r) => r.restingBed).map((r) => r.restingBed as string)));
  for (const r of w.runtime) {
    const npc = byId.get(r.npcId);
    if (!npc) continue;
    drawNpc(g, {
      npc, x: r.x, y: r.y, dir: r.dir, moving: r.moving && !r.busyWith && !r.restingBed,
      walkPhase: walk.get(r.npcId) ?? 0,
      talking: !!r.busyWith,
      dimmed: w.now < r.cooldownUntil || !!r.restingBed, // cooldown drift + crashed
    });
  }
  // Bubbles over current speakers; lanes de-overlap; hovered convo last = top.
  const anchors = layoutBubbles(
    active.map((c) => {
      const p = convoAnchor(c, w.runtime, byId);
      return { id: c.id, ax: p.x, ay: p.y, bx: p.x, by: p.y, color: c.color };
    }),
  );
  if (args.hoverConvoId) {
    const h = args.hoverConvoId;
    anchors.sort((a, b) => (a.convoId === h ? 1 : 0) - (b.convoId === h ? 1 : 0));
  }
  const rects: BubbleRect[] = [];
  const live = new Map(active.map((c) => [c.id, c]));
  for (const a of anchors) {
    // Guard: convo may vanish mid-frame (NPC deleted) between ref syncs.
    const c = live.get(a.convoId);
    if (!c) continue;
    const line = c.lines[c.cursor];
    const fading = c.fade && w.now < c.fade.until ? c.fade : null;
    if (!line && !fading) continue;
    const text = line ? line.text.slice(0, Math.floor(c.revealed)) : '';
    const border = (speaker: string): string =>
      speakerBorderColor(speaker, npcs, args.borderColors ?? {}, a.color);
    // Fade stays over whoever said the previous line. The new line uses its own head.
    if (fading) {
      const prev = placeSpeakerBubble(
        g, fading.speaker, fading.text, c, w.runtime, byId, padX, padTop, canvas.width, a.stackLane,
      );
      const alpha = Math.max(0, (fading.until - w.now) / TUNING.bubbleFadeMs);
      drawBubble(g, {
        x: prev.cx, y: prev.cy, color: border(fading.speaker), speaker: fading.speaker, text: fading.text,
        tailX: prev.tailX, alpha,
      });
    }
    if (!text || !line) continue;
    const cur = placeSpeakerBubble(
      g, line.speaker, text, c, w.runtime, byId, padX, padTop, canvas.width, a.stackLane,
    );
    drawBubble(g, {
      x: cur.cx, y: cur.cy, color: border(line.speaker), speaker: line.speaker, text,
      tailX: cur.tailX,
    });
    rects.push({ id: c.id, x0: cur.cx - cur.bw / 2, y0: cur.cy - cur.bh, x1: cur.cx + cur.bw / 2, y1: cur.cy + 12 });
  }
  // Thinking pills for claimed-but-waiting pairs (script still generating).
  const talking = new Set<string>();
  for (const c of active) {
    talking.add(c.aId);
    talking.add(c.bId);
  }
  for (const p of waitingPairs(w.runtime, talking)) {
    drawWaiting(g, p.x, p.y);
  }
  // Relationship floaters: +/- deltas rising over each NPC for 6 sim-seconds.
  const lifeMs = TUNING.floaterLifeS * 1000;
  const floaters = args.floaters.filter((f) => w.now - f.born < lifeMs);
  for (const f of floaters) {
    const r = w.runtime.find((x) => x.npcId === f.npcId);
    if (!r) continue;
    drawFloaters(g, {
      x: clampBubbleX(r.x + padX, 120, canvas.width) - padX,
      y: r.y, items: f.items,
      ageS: (w.now - f.born) / 1000, lifeS: TUNING.floaterLifeS,
    });
  }
  return { floaters, rects };
}
